import React from "react"
import {Dropdown, Menu, notification, Spin, Tooltip} from "antd";
import Button from "../../../../shared/button/Button";
import {
    DATE_FORMAT_YYYY_MM_DD_00_00_00,
    DATE_FORMAT_YYYY_MM_DD_23_59_59,
    DATE_FORMAT_YYYY_MM_DD_HH_mm_ss
} from "../../../../../model/constants/DateConstant";
import {PlusOutlined} from "@ant-design/icons";
import moment, {Moment} from "moment/moment";
import IEntry from "../../../../../model/interface/company/workload/IEntry";
import EntriesService from "../../../../../model/service/company/workload/EntriesService";
import IType, {COMPANY_WORKLOAD_TYPE_MODE_WORKING} from "../../../../../model/interface/company/workload/IType";
import SettingsService from "../../../../../model/service/SettingsService";
import {ButtonSize} from "antd/es/button";
import TypesService from "../../../../../model/service/company/workload/TypesService";
import ObligationsService from "../../../../../model/service/company/workload/ObligationsService";
import IEmployee from "../../../../../model/interface/company/IEmployee";
import {IAppState} from "../../../../../redux/store";
import selectors from "../../../../../redux/selectors";
import {connect} from "react-redux";
import ShiftsService from "../../../../../model/service/company/workload/ShiftsService";
import IObligation from "../../../../../model/interface/company/IObligation";
import LocaleText from "../../../settings/dictionary/LocaleText";
import {GeoPoint} from "../../../../shared/map/Map";
import PlansService from "../../../../../model/service/company/workload/PlansService";
import {API_FILTER_TYPE} from "../../../../../model/constants/ApiConstant";
import IHoliday from "../../../../../model/interface/ui/IHoliday";

interface IProps {
    employee: IEmployee
    types: IType[]
    date?: Moment
    onCreate?: (entry: IEntry, action: 'create' | 'update') => void,
    customRender?: (onClick: (entry?: IEntry) => void, creating: boolean) => React.ReactNode
    disabled?: boolean
    minStart?: Moment
    size?: ButtonSize,
    getText: (code: string, fallBack?: string) => string
    freeDays: IHoliday[]
    obligation: IObligation
}

interface IState {
    creating: boolean
    allowedTypes?: IType[]
    loadingAllowedTypes?: boolean
}

class EntryCreateButton extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props)
        this.state = {
            creating: false
        }
    }

    componentDidMount() {
        const {types, date, employee} = this.props
        const approvalRequiredNotSyncedTypes = types.filter(t => !t.planReportSync && t.approvalRequired && t.reportInlineEdit)
        if (approvalRequiredNotSyncedTypes.length && date) {
            this.setState({loadingAllowedTypes: true})
            PlansService.collectionList({
                depth: 4, cache: false,filters: {
                    types: {
                        value: approvalRequiredNotSyncedTypes.map(a => a.uuid),
                        field: 'type.uuid',
                        type: API_FILTER_TYPE.IN
                    }, 0: {
                        type: API_FILTER_TYPE.EQUAL,
                        field: 'employee.uuid',
                        value: employee.uuid
                    },
                    1: {
                        type: API_FILTER_TYPE.LESSER_OR_EQUAL,
                        field: 'beginAt',
                        value: date.format(DATE_FORMAT_YYYY_MM_DD_23_59_59)
                    },
                    2: {
                        type: API_FILTER_TYPE.GREATER_OR_EQUAL,
                        field: 'endAt',
                        value: date.format(DATE_FORMAT_YYYY_MM_DD_00_00_00)
                    },
                    3: {
                        type: API_FILTER_TYPE.EQUAL,
                        field: 'state',
                        value: 'approved'
                    }
                }
            }).then(response => {
                this.setState({allowedTypes: types.filter(t => !approvalRequiredNotSyncedTypes.map(a => a.uuid).includes(t.uuid) || response.results?.map(p => p.type?.uuid).includes(t.uuid)), loadingAllowedTypes: false})
            })
        }
    }

    createEntry = async (entry?: Partial<IEntry>) => {
        const {employee, date, onCreate, minStart, types, getText, freeDays, obligation} = this.props
        const currentDate = date || moment()
        this.setState({creating: true})
        let location: undefined | GeoPoint
        if (SettingsService.getValue('company', 'report_start_button_location')) {
            location = await this.getCurrentLocation()
        }

        if (entry?.id) {
            return EntriesService.getInstance().resourceUpdate(entry.id, {
                ...entry,
                endAt: entry.endAt || this.getDateWithCurrentTime(date || moment())
                    .seconds(0).format(DATE_FORMAT_YYYY_MM_DD_HH_mm_ss),
                endLatitude: location?.lat,
                endLongitude: location?.long,
            }).then((value) => {
                this.setState({creating: false})
                value.generatedEntries?.forEach(e => {
                    if (e.type && (typeof e.type as any === 'string')) {
                        e = {...e, type: types.find(t => t.uuid === e.type as any) as IType}
                    }
                    onCreate?.(e, 'create')
                })
                onCreate?.(value, 'update')
                return value
            })
        }
        const defaultType = entry?.type || SettingsService.getValue('company', 'report_default_entry_type')
            || types.find(t => t.mode === COMPANY_WORKLOAD_TYPE_MODE_WORKING && t.unlimitedFund && !t.approvalRequired)?.uuid

        if (!defaultType || !ObligationsService.isWorkloadTypeAllowed(employee.obligation, defaultType)) {
            const message = getText('company.workload.entry.create.default-type.missing', 'Typ pracovního záznamu nelze určit. Prosím, kontaktujte administrátory, aby tento problém vyřešili!')
            notification.error({
                message: message,
            })
            return this.setState({creating: false})
        }
        let startAt = (minStart || this.getDateWithCurrentTime(currentDate))
            .seconds(0)
        let endAt = undefined
        if (obligation?.attendanceEntryPreset) {
            let entriesByShifts: Promise<IEntry>[] = [];
            obligation.shifts.filter(a => ShiftsService.isShiftActive(a, currentDate, employee, freeDays)).forEach(shift => {

                startAt = ShiftsService.getStartAtDate(shift, currentDate)
                endAt = ShiftsService.getEndAtDate(shift, currentDate)
                entriesByShifts.push(EntriesService.getInstance().collectionCreate({
                    ...entry,
                    employee: employee.uuid,
                    type: defaultType,
                    startAt: startAt.format(DATE_FORMAT_YYYY_MM_DD_HH_mm_ss),
                    endAt: endAt.format(DATE_FORMAT_YYYY_MM_DD_HH_mm_ss),
                    startLatitude: location?.lat,
                    startLongitude: location?.long,
                    endLatitude: location?.lat,
                    endLongitude: location?.long,
                }).then((value) => {
                    value.generatedEntries?.forEach(e => {
                        if (e.type && (typeof e.type as any === 'string')) {
                            e = {...e, type: types.find(t => t.uuid === e.type as any) as IType}
                        }
                        onCreate?.(e, 'create')
                    })
                    onCreate?.(value, 'create')
                    return value
                }))
            })
            return Promise.all(entriesByShifts).then(() => {
                this.setState({creating: false})
            })

        }
        return EntriesService.getInstance().collectionCreate({
            ...entry,
            employee: employee.uuid,
            type: defaultType,
            startAt: startAt.format(DATE_FORMAT_YYYY_MM_DD_HH_mm_ss),
            endAt: endAt,
            startLatitude: location?.lat,
            startLongitude: location?.long,
        }).then((value) => {
            this.setState({creating: false})
            onCreate?.(value, 'create')
            return value
        })
    }

    getCurrentLocation = async (): Promise<GeoPoint | undefined> => {
        if (!navigator.geolocation) {
            console.error("Geolocation is not supported by this browser.");
            return undefined;
        }

        // 1. Determine if a prompt is likely to appear
        let dynamicTimeout = 8000; // Default snappy timeout

        try {
            // query() is not supported in all browsers (like older Safari),
            // so we wrap it in a try/catch
            const permissionStatus = await navigator.permissions.query({name: 'geolocation'});

            if (permissionStatus.state === 'prompt') {
                console.log("Permission not yet granted. Extending timeout for user prompt.");
                dynamicTimeout = 25000; // Give the user 25 seconds to click "Allow"
            }
        } catch (e) {
            // If permissions API isn't supported, use a safe middle-ground
            dynamicTimeout = 15000;
        }

        // 2. Execute the location request with the dynamic timeout
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const {latitude, longitude} = position.coords;
                    resolve({lat: latitude, long: longitude});
                },
                (error) => {
                    // If it still times out, try one last time with low accuracy (no GPS)
                    if (error.code === error.TIMEOUT) {
                        console.warn("Location request timed out. Retrying with low accuracy...");
                        navigator.geolocation.getCurrentPosition(
                            (pos) => resolve({lat: pos.coords.latitude, long: pos.coords.longitude}),
                            (err) => {
                                console.error("Final error:", err.message);
                                resolve(undefined);
                            },
                            {enableHighAccuracy: false, timeout: 5000}
                        );
                    } else {
                        console.error("Geolocation error:", error.message);
                        resolve(undefined);
                    }
                },
                {
                    enableHighAccuracy: true,
                    timeout: dynamicTimeout, // 👈 Uses the calculated timeout
                    maximumAge: 30000        // Accept recent cache (30s) to speed up response
                }
            );
        });
    };

    getDateWithCurrentTime(date: Moment, time?: Moment) {
        return (time || moment()).year(date.year()).month(date.month()).date(date.date())
    }

    buildTypeOptions = () => {
        const {employee, types, getText} = this.props
        const {allowedTypes} = this.state
        return TypesService.listReportEditableType(allowedTypes || types)
            .filter(type => ObligationsService.isWorkloadTypeAllowed(employee.obligation, type))
            .sort((a, b) => getText(a.title) > getText(b.title) ? 1 : -1)
            .map(type => ({
                value: type.uuid,
                label: <LocaleText code={type.title}/>
            }))
    }

    render() {
        const {creating, loadingAllowedTypes} = this.state
        const {disabled} = this.props

        const types = this.buildTypeOptions()
        if (types.length > 1 && !disabled && !creating) {
            return <Dropdown trigger={['hover']} mouseEnterDelay={0.5} overlay={<Menu
                onClick={(type) => this.createEntry({type: type.key as any})}>
                {loadingAllowedTypes ? <Spin spinning={true} className={'w-100 h-100'}/> : types.map(type => (
                    <Menu.Item key={type.value}>
                        {type.label}
                    </Menu.Item>
                ))}
            </Menu>}>
                {this.renderButton()}
            </Dropdown>
        }
        return this.renderButton()
    }

    private renderButton() {
        const {creating} = this.state
        const {customRender, disabled, size} = this.props
        return (
            customRender ? customRender(this.createEntry, creating) : <Tooltip title={<LocaleText code={'company.workload.report.attendance.entry'} fallback={'Příchod'}/>}>
                <Button icon={<PlusOutlined/>}
                        shape={'circle'}
                        disabled={disabled}
                        loading={creating}
                        onClick={() => this.createEntry()}
                        size={size}
                        className={"btn-outlined"}/>
            </Tooltip>
        );
    }
}


const mapStateToProps = (state: IAppState) => {
    return {
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)

    }
}

export default connect(mapStateToProps)(EntryCreateButton)
